/**
 * ============================================================================
 * Simplified Cart Management Logic
 * ============================================================================
 * Handles promo items, cart updates, section refreshing, and gift wrapping
 */

// --- Configuration ---
// Define these at the very top to ensure they're available for all functions
const SELECTORS = {
  PROMO: ".lt-cart-promo-product",
  CART_SAMPLES: "#lt-cart-samples",
  CART_CAROUSEL: "#lt-cart-product-carousel",
  CART_PROMO_SLIDER: "#lt-cart-promo-slider",
  GIFT_WRAP_CONTENT: ".lt-cart-gift-wrap .gift-content",
  GIFT_WRAP_MESSAGE: "#gift-wrap-message",
  GIFT_WRAP_AGREE: "#CartGiftWrapAgree",
  GIFT_NOTE: "#GiftNote",
  GIFT_BOX_AGREE: "#CartGiftBoxAgree",
  LOADER: "#loader", // Original loader for samples
  LOADER_GIFTWRAP: "#loader-giftwrap", // New loader for gift wrap
  LOADER_PROMO: ".loader-promo", // Loaders inside promo banners
  CHECKOUT_CONTAINER: "#sticky-checkout-container",
  CART_CTAS: ".cart__ctas",
  MAIN_CART_ITEMS: "#main-cart-items",
  MAIN_CART_FOOTER: "#main-cart-footer",
  CART_ICON_BUBBLE: "#cart-icon-bubble",
  PROMO_CODE_MESSAGE: "#promo-code-message",
};

const EVENTS = {
  CART_UPDATED: "cart:updated",
  SECTION_LOAD: "shopify:section:load",
  SECTION_SELECT: "shopify:section:select",
  SHOPIFY_CART_UPDATE: "cart:update",
};

// PubSub events from Dawn theme
window.PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
  cartUpdate: "cart:update",
  cartError: "cart:error",
  sectionRender: "section:render",
  cartItemAdded: "cart:item-added",
  cartItemError: "cart:item-error",
};

// Add this at the top of your script, after the constants
window.isReloadScheduled = false;

/**
 * Setup sticky checkout button
 */
function setupStickyCheckout() {
  const ctaElement = document.querySelector(SELECTORS.CART_CTAS);
  const positioner = document.getElementById(
    SELECTORS.CHECKOUT_CONTAINER.slice(1)
  );

  if (!ctaElement || !positioner) return;

  const checkoutBtnCallback = (entries) => {
    entries.forEach((entry) => {
      const isBottomVisible =
        entry.boundingClientRect.bottom <= window.innerHeight;
      positioner.classList.toggle("floating-cta", !isBottomVisible);
    });
  };

  const options = {
    root: null,
    threshold: 1,
  };

  const checkoutBtnObserver = new IntersectionObserver(
    checkoutBtnCallback,
    options
  );
  checkoutBtnObserver.observe(ctaElement);
}

/**
 * Initialize sliders using Splide library
 */
function initializeSliders() {
  console.log("Initializing all sliders");

  // Initialize sample slider
  const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
  if (cartSampleDiv) {
    console.log("Initializing cart samples slider");
    try {
      if (typeof Splide !== "undefined") {
        // Check if Splide is already initialized on this element
        if (!cartSampleDiv.classList.contains("is-initialized")) {
          new Splide(SELECTORS.CART_SAMPLES, {
            perPage: 3,
            gap: "1rem",
            pagination: false,
            breakpoints: {
              640: {
                perPage: 2,
              },
            },
          }).mount();
        }
      } else {
        console.warn(
          "Splide library not loaded yet. Slider initialization skipped."
        );
      }
    } catch (e) {
      console.error("Error initializing cart samples slider:", e);
    }
  }

  // Initialize product carousel
  const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);
  if (cartProductCarousel) {
    console.log("Initializing cart product carousel");
    try {
      if (typeof Splide !== "undefined") {
        // Check if Splide is already initialized on this element
        if (!cartProductCarousel.classList.contains("is-initialized")) {
          new Splide(SELECTORS.CART_CAROUSEL, {
            perPage: 4,
            gap: "1rem",
            pagination: false,
            breakpoints: {
              1024: {
                perPage: 3,
              },
              768: {
                perPage: 2,
              },
              480: {
                perPage: 1,
              },
            },
          }).mount();
        }
      } else {
        console.warn(
          "Splide library not loaded yet. Slider initialization skipped."
        );
      }
    } catch (e) {
      console.error("Error initializing cart product carousel:", e);
    }
  }
}

/**
 * Initialize promo handler
 */
function initializePromoHandler() {
  console.log("Initializing promo handler");

  // Run initial check immediately only once
  if (!window.initialPromoCheckDone) {
    performInitialCheck();
    window.initialPromoCheckDone = true;
  }

  // Set up event listeners for quantity changes
  document.addEventListener("change", function (event) {
    if (event.target.matches('input[name="updates[]"]')) {
      if (document.querySelector(SELECTORS.PROMO) && !window.isReloadScheduled) {
        // Give the cart time to update
        setTimeout(() => checkPromoThresholdAndRemove(), 500);
      }
    }
  });

  // Subscribe to cart update events
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // Skip if we're reloading or handling our own events
    if (
      window.isReloadScheduled ||
      event.source === "lt-cart-js-reload" ||
      event.source === "lt-cart-js-promo-check"
    ) {
      return;
    }

    console.log(
      "Dawn PubSub cart update event received in promo handler:",
      event
    );

    // Cart items HTML was re-rendered, mount the new (hidden) sliders
    initializeCartBlocksNow();

    if (document.querySelector(SELECTORS.PROMO)) {
      setTimeout(() => checkPromoThresholdAndRemove(), 250);
    }
  });

  // Listen to section load events for theme editor
  document.addEventListener(EVENTS.SECTION_LOAD, function (event) {
    if (event.target.querySelector(SELECTORS.PROMO)) {
      setTimeout(() => checkPromoThresholdAndRemove(), 50);
    }
  });
}

// Track initialization and store unsubscribers
window.cartInitialized = false;
let cartUpdateUnsubscriber = null;

/**
 * Initialize everything on DOM ready
 */
function initializeAll() {
  // Prevent multiple initializations
  if (window.cartInitialized) {
    console.log("Cart already initialized, skipping");
    return;
  }

  console.log("Initializing all cart components");
  window.cartInitialized = true;
  window.initialPromoCheckDone = false;
  window.isReloadScheduled = false;

  // Set up the main PubSub event handler first
  setupCartEventListeners();

  // Wait a short moment for the DOM to stabilize
  setTimeout(() => {
    // Initialize all components
    initializeCartBlocks();
    initializePromoHandler();
    initializeGiftWrap();
    setupStickyCheckout();

    // Initialize sliders with a more reliable approach
    initializeSliders();

    // Sometimes sliders need a second attempt if the first one fails
    setTimeout(initializeSliders, 500);
  }, 100);
}

/**
 * Set up event listeners using Dawn's PubSub system
 */
function setupCartEventListeners() {
  // Clean up any existing subscription
  if (cartUpdateUnsubscriber) {
    cartUpdateUnsubscriber();
    cartUpdateUnsubscriber = null;
  }

  // Subscribe to cart update events from Dawn
  cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    console.log("Dawn cart update event received:", event);

    // Dawn's product form only refreshes the cart drawer, so reload the cart page
    // after a promo product is added from a promo banner
    if (
      event.source === "product-form" &&
      !window.isReloadScheduled &&
      isPromoVariant(event.productVariantId)
    ) {
      rememberPromoAddedSlideIndex();
      reloadAfterCartAction(event.cartData);
      return;
    }

    // Don't respond to our own events or if we're about to reload
    if (
      event.source === "cart-items" ||
      event.source === "lt-cart-js-reload" ||
      event.source === "lt-cart-js-promo-check" ||
      window.isReloadScheduled
    ) {
      return;
    }

    // Initialize cart blocks and check promo thresholds
    initializeCartBlocks();
    initializePromoHandler();

    // Make sure to re-check gift wrap status after cart updates
    initializeGiftWrap();

    // Dispatch our custom event for backward compatibility
    document.dispatchEvent(new CustomEvent(EVENTS.CART_UPDATED));
  });

  // Subscribe to cart item added events if needed
  subscribe(PUB_SUB_EVENTS.cartItemAdded, (event) => {
    console.log("Dawn cart item added event received:", event);
  });

  // Listen for section renders which happen when cart is updated
  subscribe(PUB_SUB_EVENTS.sectionRender, (event) => {
    console.log("Section render event received:", event);

    // Check if the gift wrap section was re-rendered
    const giftWrapContent = document.querySelector(SELECTORS.GIFT_WRAP_CONTENT);
    if (giftWrapContent) {
      // Re-initialize gift wrap functionality
      setTimeout(() => initializeGiftWrap(), 100);
    }
  });
}

// Run initialization based on document state
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeAll);
} else {
  // Handle cases where the script loads after DOMContentLoaded
  initializeAll();
}

/**
 * Check if a variant belongs to one of the promo banner forms
 */
function isPromoVariant(variantId) {
  if (!variantId) return false;
  return Array.from(
    document.querySelectorAll(`${SELECTORS.PROMO} form input[name="id"]`)
  ).some((input) => input.value === String(variantId));
}

/**
 * Keep the added promo slide index across the page reload
 */
function rememberPromoAddedSlideIndex() {
  if (window.ltPromoAddedSlideIndex == null) return;
  try {
    sessionStorage.setItem(
      "ltPromoAddedSlideIndex",
      String(window.ltPromoAddedSlideIndex)
    );
  } catch (e) {}
}

function takePromoAddedSlideIndex() {
  let index = window.ltPromoAddedSlideIndex;
  window.ltPromoAddedSlideIndex = null;
  try {
    const stored = sessionStorage.getItem("ltPromoAddedSlideIndex");
    sessionStorage.removeItem("ltPromoAddedSlideIndex");
    if (index == null && stored !== null) index = parseInt(stored, 10);
  } catch (e) {}
  return Number.isNaN(index) ? null : index;
}

// Remember which promo slide was added, so the re-rendered slider can swipe to the next one
document.addEventListener(
  "submit",
  (event) => {
    const slide = event.target.closest(`${SELECTORS.CART_PROMO_SLIDER} .splide__slide`);
    if (!slide) return;
    window.ltPromoAddedSlideIndex = Array.from(slide.parentElement.children).indexOf(slide);
  },
  true
);

// Backward compatibility event listener
document.addEventListener(EVENTS.SHOPIFY_CART_UPDATE, () => {
  initializeCartBlocks();
  initializePromoHandler();
});

// Make functions available globally
window.sampleBtn = sampleBtn;
window.submitPromo = submitPromo;

/**
 * Clean up event listeners
 */
function cleanupCartEventListeners() {
  if (cartUpdateUnsubscriber) {
    cartUpdateUnsubscriber();
    cartUpdateUnsubscriber = null;
  }
}

// Make this function available globally
window.cleanupCartEventListeners = cleanupCartEventListeners;

// --- Core Utility Functions ---

/**
 * Universal function to fetch cart data
 */
function fetchCart() {
  return fetch(`${window.location.pathname}.js`).then((response) => {
    if (!response.ok)
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    return response.json();
  });
}

/**
 * Simplified update cart function
 */
function updateCart(params) {
  const { action = "update", data } = params;

  // Determine URL based on action
  let url;
  switch (action) {
    case "add":
      url = `${window.location.pathname}/add.js`;
      break;
    case "change":
      url = `${window.location.pathname}/change.js`;
      break;
    case "update":
    default:
      url = `${window.location.pathname}/update.js`;
  }

  // Create a copy of the data
  const requestData = { ...data };

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(requestData),
  }).then((response) => {
    if (!response.ok)
      throw new Error(`Network response was not ok: ${response.statusText}`);
    return response.json();
  });
}

/**
 * Reload the page after cart actions
 */
function reloadAfterCartAction(data, delay = 100, queryParam = null) {
  console.log("Scheduling page reload after cart action");

  // Prevent multiple reloads
  if (window.isReloadScheduled) {
    console.log(
      "A page reload is already scheduled, skipping additional reload"
    );
    return data;
  }

  // Mark that we're going to reload
  window.isReloadScheduled = true;

  // Publish the event if not reloading immediately
  if (delay > 50) {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      cart: data,
      source: "lt-cart-js-reload",
    });
  }

  // Force reload after delay
  const reloadTimeout = setTimeout(() => {
    console.log("Executing page reload now!");
    clearTimeout(reloadTimeout);

    // Add query param if provided
    if (queryParam) {
      const reloadUrl = window.location.pathname + "?" + queryParam;
      window.location.href = reloadUrl;
    } else {
      window.location.reload(true); // Force reload from server
    }
  }, delay);

  return data;
}

/**
 * Update cart attributes
 */
function updateCartAttribute(key, value, reload = false) {
  return updateCart({
    action: "update",
    data: { attributes: { [key]: value } },
  }).then((data) => {
    if (reload) {
      return reloadAfterCartAction(data);
    }
    return data;
  });
}

/**
 * Add item to cart - simplified to always reload
 */
function addItemToCart(variantId, quantity = 1) {
  return updateCart({
    action: "add",
    data: {
      items: [{ id: variantId, quantity }],
    },
  }).then((data) => reloadAfterCartAction(data));
}

/**
 * Remove item from cart - simplified to always reload
 */
function removeItemFromCart(variantId) {
  console.log(`Attempting to remove item with variant ID: ${variantId}`);

  return fetchCart().then((cart) => {
    // Use loose comparison to handle string/number type differences
    const itemToRemove = cart.items.find(
      (item) => item.variant_id == variantId
    );

    if (itemToRemove) {
      console.log(
        `Found item to remove: ${itemToRemove.title} (key: ${itemToRemove.key})`
      );

      return updateCart({
        action: "change",
        data: {
          id: itemToRemove.key,
          quantity: 0,
        },
      }).then((data) => reloadAfterCartAction(data));
    } else {
      console.warn(`Item with variantId ${variantId} not found in cart.`);
      // Reload anyway to ensure consistent state
      return reloadAfterCartAction(cart);
    }
  });
}

/**
 * Read promo data from all promo banners on the page
 */
function getPromoBanners() {
  return Array.from(document.querySelectorAll(SELECTORS.PROMO))
    .map((element) => ({
      element,
      productId: parseInt(element.dataset.promoProductId, 10),
      threshold: parseInt(element.dataset.promoThreshold, 10),
    }))
    .filter((promo) => {
      // Threshold 0 is valid: that promo is always available and never removed
      if (isNaN(promo.productId) || isNaN(promo.threshold) || promo.threshold < 0) {
        console.warn(
          "Promo check skipped: Missing or invalid data attributes on",
          promo.element
        );
        return false;
      }
      return true;
    });
}

function setPromoLoaders(visible) {
  document.querySelectorAll(SELECTORS.LOADER_PROMO).forEach((loader) => {
    loader.style.display = visible ? "block" : "none";
  });
}

/**
 * Swipe the promo slider from the promo that was just added
 * to the next one that's not in the cart yet
 */
function swipeToNextPromo(slider, addedIndex) {
  const banners = slider.root.querySelectorAll(SELECTORS.PROMO);
  if (banners[addedIndex]?.dataset.promoInCart !== "true") return;

  for (let step = 1; step < banners.length; step++) {
    const index = (addedIndex + step) % banners.length;
    if (banners[index].dataset.promoInCart !== "true") {
      setTimeout(() => slider.go(index), 300);
      return;
    }
  }
}

/**
 * Remove promo items from cart by line item key and reload
 */
function removePromoItems(itemKeys, queryParam = null) {
  if (!itemKeys.length) return Promise.resolve(null);

  console.log("Removing promo items with keys:", itemKeys);

  const updates = {};
  itemKeys.forEach((key) => {
    updates[key] = 0;
  });

  return updateCart({
    action: "update",
    data: { updates },
  })
    .then((updatedData) => {
      console.log("Cart update response after promo removal:", updatedData);
      return reloadAfterCartAction(updatedData, 100, queryParam);
    })
    .catch((error) => {
      console.error("Error during promo removal:", error);
      if (window.showCartError) {
        window.showCartError(
          "Sorry, we could not remove the promo item. Please try again."
        );
      }
      window.location.reload();
      return null;
    });
}

/**
 * Check all promo thresholds and remove promo items whose threshold is not met.
 * The cart total excludes every promo product, so one promo can't unlock another.
 */
function checkPromoThresholdAndRemove(isInitialCheck = false) {
  const promos = getPromoBanners();
  if (!promos.length) return;

  setPromoLoaders(true);

  return fetchCart()
    .then((cart) => {
      const promoProductIds = promos.map((promo) => promo.productId);
      let totalWithoutPromo = cart.total_price;

      cart.items.forEach((item) => {
        if (promoProductIds.includes(item.product_id)) {
          totalWithoutPromo -= item.final_line_price;
        }
      });

      console.log(
        `Cart total without promos: ${totalWithoutPromo} (${cart.currency})`
      );

      const keysToRemove = [];
      let promoInCart = false;

      promos.forEach((promo) => {
        const promoItem = cart.items.find(
          (item) => item.product_id === promo.productId
        );
        if (!promoItem) return;

        promoInCart = true;
        if (totalWithoutPromo < promo.threshold) {
          console.log(
            `Threshold ${promo.threshold} NOT MET - removing promo item with key: ${promoItem.key}`
          );
          keysToRemove.push(promoItem.key);
        }
      });

      if (keysToRemove.length) {
        setPromoLoaders(false);
        return removePromoItems(
          keysToRemove,
          isInitialCheck ? "promo_check=true" : "promo_removed=true"
        );
      }

      setPromoLoaders(false);

      if (promoInCart && isInitialCheck) {
        const url = new URL(window.location);
        url.searchParams.set("promo_check", "true");
        window.history.replaceState({}, document.title, url);
      }

      return cart;
    })
    .catch((error) => {
      console.error("Error checking cart state:", error);
      setPromoLoaders(false);
    });
}

/**
 * Perform initial check on page load
 */
function performInitialCheck() {
  console.log("Initial check");

  const promoElements = document.querySelectorAll(SELECTORS.PROMO);
  if (!promoElements.length) {
    console.log("Initial check: No promo section element found.");
    return;
  }

  // Liquid already knows which promo items should be removed
  const initialRemoveKeys = Array.from(promoElements)
    .map((element) => element.dataset.promoInitialRemoveKey)
    .filter(Boolean);

  if (initialRemoveKeys.length) {
    console.log("Initial check: Promo removal keys found on page load.");
    return removePromoItems(initialRemoveKeys);
  }

  return checkPromoThresholdAndRemove(true);
}

/**
 * Handle sample button click
 */
function sampleBtn(event, variant, index) {
  const sample = event.target.closest(".sample-product");
  const variantExists = sample.classList.contains("sample-in-cart");
  const loader = document.querySelector(SELECTORS.LOADER);

  // Show loader
  if (loader) loader.style.display = "block";

  if (variantExists) {
    // Remove item and reload
    updateCart({
      action: "change",
      data: {
        line: index,
        quantity: 0,
      },
    })
      .then((data) => {
        if (loader) loader.style.display = "none";
        reloadAfterCartAction(data);
      })
      .catch(() => {
        if (loader) loader.style.display = "none";
        window.location.reload();
      });
  } else {
    // Add item and reload
    updateCart({
      action: "add",
      data: {
        items: [{ id: variant, quantity: 1 }],
      },
    })
      .then((data) => {
        if (loader) loader.style.display = "none";
        reloadAfterCartAction(data);
      })
      .catch(() => {
        if (loader) loader.style.display = "none";
        window.location.reload();
      });
  }
}

/**
 * Submit promo code using the new Cart Ajax API discount endpoint
 */
function submitPromo(event) {
  event.target.classList.add("submitting");
  const spinner = event.target.querySelector(".loading__spinner");
  if (spinner) spinner.classList.remove("hidden");

  const discountCode = document.getElementById("apply-discount").value;
  if (!discountCode) {
    if (spinner) spinner.classList.add("hidden");
    event.target.classList.remove("submitting");
    return;
  }

  async function applyDiscountCode(discountCode) {
    try {
      // Get cart section IDs for updating UI
      const mainCartItems = document.querySelector(SELECTORS.MAIN_CART_ITEMS);
      const mainCartFooter = document.querySelector(SELECTORS.MAIN_CART_FOOTER);

      // Use the new Cart Ajax API discount endpoint to validate and apply discount
      const discountResponse = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountCode,
          sections:
            mainCartItems && mainCartFooter
              ? [mainCartItems.dataset.id, mainCartFooter.dataset.id]
              : [],
        }),
      });

      if (!discountResponse.ok) {
        // Handle error response (invalid discount code)
        const errorData = await discountResponse.json();
        console.log("Discount validation failed:", errorData);

        // Show error message
        const promoMessage = document.getElementById(
          SELECTORS.PROMO_CODE_MESSAGE.slice(1)
        );
        if (promoMessage) promoMessage.style.display = "block";
        console.log("Invalid discount code!");

        return { ok: false, error: errorData };
      }

      const responseData = await discountResponse.json();
      console.log("🚀 ~ discount response:", responseData);

      // Check if discount was successfully applied
      if (responseData && discountResponse.status === 200) {
        console.log("Valid discount code applied!");

        // Set the discount cookie for checkout
        document.cookie = `discount_code=${discountCode}; path=/`;

        // Update cart sections if they exist in the response
        if (responseData.sections) {
          if (
            mainCartItems &&
            responseData.sections[mainCartItems.dataset.id]
          ) {
            document.querySelector(".main-cart-items").innerHTML =
              responseData.sections[mainCartItems.dataset.id];
          }
          if (
            mainCartFooter &&
            responseData.sections[mainCartFooter.dataset.id]
          ) {
            document.querySelector(".main-cart-footer").innerHTML =
              responseData.sections[mainCartFooter.dataset.id];
          }
        }

        // Reload the page to show the discount
        return reloadAfterCartAction(responseData);
      } else {
        // Discount code exists but may not be applicable to current cart
        console.log("Discount code not applicable to current cart");

        const promoMessage = document.getElementById(
          SELECTORS.PROMO_CODE_MESSAGE.slice(1)
        );
        if (promoMessage) promoMessage.style.display = "block";

        return { ok: false, error: "Discount not applicable" };
      }
    } catch (error) {
      console.error("Error applying discount code:", error);

      // Show error message
      const promoMessage = document.getElementById(
        SELECTORS.PROMO_CODE_MESSAGE.slice(1)
      );
      if (promoMessage) promoMessage.style.display = "block";

      return { ok: false, error: error.message };
    }
  }

  applyDiscountCode(discountCode)
    .catch((error) => {
      console.error("Discount code error:", error);
    })
    .finally(() => {
      event.target.classList.remove("submitting");
      if (spinner) spinner.classList.add("hidden");
    });
}

/**
 * Initialize cart UI blocks with observer pattern
 */
function initializeCartBlocks() {
  console.log("Initializing cart blocks");

  // First attempt to initialize immediately
  initializeCartBlocksNow();

  // Set up observer for late-rendered elements
  const cartObserver = new MutationObserver((mutations) => {
    const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
    const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);
    const cartPromoSlider = document.querySelector(SELECTORS.CART_PROMO_SLIDER);

    // If any slider element is found, initialize
    if (
      (cartSampleDiv && !cartSampleDiv.classList.contains("is-initialized")) ||
      (cartProductCarousel &&
        !cartProductCarousel.classList.contains("is-initialized")) ||
      (cartPromoSlider && !cartPromoSlider.classList.contains("is-initialized"))
    ) {
      console.log(
        "Cart slider elements found in DOM mutation, initializing..."
      );
      initializeCartBlocksNow();

      // Wait to confirm initialization was successful
      setTimeout(() => {
        const sampleInitialized =
          !cartSampleDiv || cartSampleDiv.classList.contains("is-initialized");
        const carouselInitialized =
          !cartProductCarousel ||
          cartProductCarousel.classList.contains("is-initialized");
        const promoSliderInitialized =
          !cartPromoSlider ||
          cartPromoSlider.classList.contains("is-initialized");

        // If all are initialized, disconnect observer
        if (sampleInitialized && carouselInitialized && promoSliderInitialized) {
          console.log("All sliders initialized, disconnecting observer");
          cartObserver.disconnect();
        }
      }, 500);
    }
  });

  // Start observing the document
  cartObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Wait for Splide to be available
  if (typeof Splide === "undefined") {
    console.log("Splide not yet available, waiting...");
    const splideCheckInterval = setInterval(() => {
      if (typeof Splide !== "undefined") {
        console.log("Splide now available");
        clearInterval(splideCheckInterval);
        initializeCartBlocksNow();
      }
    }, 200);
  }
}

/**
 * Immediate initialization of cart UI elements
 */
function initializeCartBlocksNow() {
  const cartSampleDiv = document.querySelector(SELECTORS.CART_SAMPLES);
  const cartProductCarousel = document.querySelector(SELECTORS.CART_CAROUSEL);

  // Initialize sample slider
  if (cartSampleDiv && !cartSampleDiv.classList.contains("is-initialized")) {
    console.log("Initializing cart samples slider");
    try {
      if (typeof Splide !== "undefined") {
        new Splide(SELECTORS.CART_SAMPLES).mount();
        cartSampleDiv.classList.add("is-initialized");
      } else {
        console.warn("Splide library not loaded yet for samples slider");
      }
    } catch (e) {
      console.error("Error initializing cart samples slider:", e);
    }
  }

  // Initialize product carousel
  if (
    cartProductCarousel &&
    !cartProductCarousel.classList.contains("is-initialized")
  ) {
    console.log("Initializing cart product carousel");
    try {
      if (typeof Splide !== "undefined") {
        new Splide(SELECTORS.CART_CAROUSEL).mount();
        cartProductCarousel.classList.add("is-initialized");
      } else {
        console.warn("Splide library not loaded yet for product carousel");
      }
    } catch (e) {
      console.error("Error initializing cart product carousel:", e);
    }
  }

  // Initialize promo product slider
  const cartPromoSlider = document.querySelector(SELECTORS.CART_PROMO_SLIDER);
  if (cartPromoSlider && !cartPromoSlider.classList.contains("is-initialized")) {
    console.log("Initializing cart promo slider");
    try {
      if (typeof Splide !== "undefined") {
        const addedIndex = takePromoAddedSlideIndex();

        // Start on the promo that was just added, so the swipe to the next one is visible
        if (addedIndex != null) {
          const options = JSON.parse(cartPromoSlider.dataset.splide || "{}");
          options.start = addedIndex;
          cartPromoSlider.dataset.splide = JSON.stringify(options);
        }

        const promoSlider = new Splide(SELECTORS.CART_PROMO_SLIDER).mount();
        cartPromoSlider.classList.add("is-initialized");

        if (addedIndex != null) swipeToNextPromo(promoSlider, addedIndex);
      } else {
        console.warn("Splide library not loaded yet for promo slider");
      }
    } catch (e) {
      console.error("Error initializing cart promo slider:", e);
    }
  }

  // Initialize gift message toggle
  const giftMsgDetail = document.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
  const giftMsgAgree = document.querySelector(SELECTORS.GIFT_WRAP_AGREE);

  if (giftMsgDetail && giftMsgAgree) {
    giftMsgAgree.addEventListener("change", function () {
      if (giftMsgAgree.checked) {
        giftMsgDetail.setAttribute("open", "");
      } else {
        giftMsgDetail.removeAttribute("open");
      }
    });
  }

  // Character counter for gift note
  function charCount() {
    const note = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));
    if (!note) return;

    const textEntered = note.value;
    const msg = document.getElementById("msg");
    if (msg && note.maxLength) {
      const counter = note.maxLength - textEntered.length;
      msg.textContent = counter + " characters left";
    }
  }

  const giftNote = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));
  if (giftNote) {
    giftNote.addEventListener("keyup", charCount, false);
  }
}

/**
 * Gift wrap functionality
 */
function initializeGiftWrap() {
  console.log("Initializing gift wrap functionality");

  // Simple initialization - server-side Liquid handles the checkbox state
  const giftNote = document.getElementById(SELECTORS.GIFT_NOTE.slice(1));

  if (giftNote && typeof window.updateGiftNoteCounter === "function") {
    window.updateGiftNoteCounter(giftNote);
  }

  // Also listen for shopify section loads, which happen when the cart is updated
  document.removeEventListener(EVENTS.SECTION_LOAD, onSectionLoad);
  document.addEventListener(EVENTS.SECTION_LOAD, onSectionLoad);

  // Make the handler function available globally
  window.handleGiftWrap = function (checkbox) {
    // Store the original checked state
    const originalState = checkbox.checked;
    const isChecked = originalState;

    console.log(`Gift wrap checkbox changed to: ${isChecked}`);
    console.log("About to send cart update request...");

    // Get loader
    let loader = document.querySelector(SELECTORS.LOADER_GIFTWRAP);
    if (!loader) {
      loader = document.querySelector(SELECTORS.LOADER);
    }

    // Show loader if it exists
    if (loader) loader.style.display = "block";

    // Always update the cart attribute first
    const attributeData = {
      attributes: {
        "gift-wrapping": isChecked ? "on" : "",
        "Gift Wrap": isChecked
      }
    };

    // Get the gift wrap content element
    const giftWrapContent = document.querySelector(SELECTORS.GIFT_WRAP_CONTENT);

    updateCart({
      action: "update",
      data: attributeData,
    })
      .then(() => {
        // Only try to add/remove product if we have valid gift wrap content with an ID
        if (
          giftWrapContent &&
          giftWrapContent.dataset &&
          giftWrapContent.dataset.giftWrapId &&
          giftWrapContent.dataset.giftWrapId !== "0"
        ) {
          const giftWrapId = giftWrapContent.dataset.giftWrapId;
          console.log(`Processing gift wrap product with ID: ${giftWrapId}`);

          if (isChecked) {
            // Add gift wrap product
            return updateCart({
              action: "add",
              data: { items: [{ id: giftWrapId, quantity: 1 }] },
            });
          } else {
            // Find and remove the gift wrap product
            return fetchCart().then((cart) => {
              const giftWrapItem = cart.items.find(
                (item) => item.variant_id == giftWrapId
              );
              if (giftWrapItem) {
                return updateCart({
                  action: "change",
                  data: { id: giftWrapItem.key, quantity: 0 },
                });
              }
              return cart;
            });
          }
        } else {
          console.log(
            "No gift wrap product linked or ID is 0, only attribute updated"
          );
          return Promise.resolve(); // No product to add/remove
        }
      })
      .then(() => {
        console.log("Gift wrap processing complete");
        if (loader) loader.style.display = "none";

        // Only reload if we have a product to update
        const hasValidProduct =
          giftWrapContent &&
          giftWrapContent.dataset &&
          giftWrapContent.dataset.giftWrapId &&
          giftWrapContent.dataset.giftWrapId !== "0";

        if (hasValidProduct) {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error handling gift wrap:", error);
        if (loader) loader.style.display = "none";
        checkbox.checked = !originalState;
        alert("Could not update gift wrap. Please try again.");
      });
  };

  // Initialize gift note handling
  if (giftNote) {
    // Define debounce function
    function debounce(func, delay) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
      };
    }

    // Create debounced handler and make it global
    window.handleGiftNoteChange = debounce(function (input) {
      updateCartAttribute("Gift Note", input.value);
    }, 750);

    // Add event listener for gift note textarea
    giftNote.addEventListener("input", function (e) {
      window.handleGiftNoteChange(this);
    });
  }

  // Setup character counter
  window.updateGiftNoteCounter = function (textarea) {
    const msg = document.getElementById("msg");
    if (msg && textarea.maxLength) {
      const counter = textarea.maxLength - textarea.value.length;
      msg.textContent = counter + " characters left";
    }
  };

  // Setup gift message toggle
  window.toggleGiftWrapMessage = function (checkbox) {
    const giftMsgDetail = document.querySelector(SELECTORS.GIFT_WRAP_MESSAGE);
    if (giftMsgDetail) {
      if (checkbox.checked) {
        giftMsgDetail.setAttribute("open", "");
      } else {
        giftMsgDetail.removeAttribute("open");
      }
    }
  };
}

/**
 * Section load handler - simplified to just update character counter
 */
function onSectionLoad(event) {
  // Just update character counter if gift note exists
  const giftNote = event.target.querySelector(SELECTORS.GIFT_NOTE);
  if (giftNote && typeof window.updateGiftNoteCounter === "function") {
    window.updateGiftNoteCounter(giftNote);
  }
}

// PubSub system from Dawn theme
function subscribe(eventName, callback) {
  const eventCallbacks = window.Shopify?.designMode
    ? (window.Shopify.eventCallbacks = window.Shopify.eventCallbacks || {})
    : (document.eventCallbacks = document.eventCallbacks || {});
  eventCallbacks[eventName] = eventCallbacks[eventName] || [];
  eventCallbacks[eventName].push(callback);

  return function unsubscribe() {
    const callbacks = eventCallbacks[eventName];
    const index = callbacks.indexOf(callback);
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
  };
}

function publish(eventName, data) {
  const eventCallbacks = window.Shopify?.designMode
    ? (window.Shopify.eventCallbacks = window.Shopify.eventCallbacks || {})
    : (document.eventCallbacks = document.eventCallbacks || {});

  if (eventCallbacks[eventName]) {
    eventCallbacks[eventName].forEach((callback) => callback(data));
  }
}
